import React, { useState, useEffect } from 'react';
import Layout from '../../components/ui/Layout';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import ServiceListingsTab from './components/ServiceListingsTab';
import RestaurantListingsTab from './components/RestaurantListingsTab';
import PropertyListingsTab from './components/PropertyListingsTab';
import TourPackagesTab from './components/TourPackagesTab';
import UserAccountsTab from './components/UserAccountsTab';
import OrderIntelligenceTab from './components/OrderIntelligenceTab';

const VerificationsApprovals = () => {
  const [activeTab, setActiveTab] = useState('services');
  const [notifications, setNotifications] = useState([]);
  const [lastSync, setLastSync] = useState(new Date());

  const tabs = [
    {
      id: 'services',
      label: 'Service Listings',
      icon: 'Tool',
      component: ServiceListingsTab
    },
    {
      id: 'restaurants',
      label: 'Restaurant Listings',
      icon: 'UtensilsCrossed',
      component: RestaurantListingsTab
    },
    {
      id: 'properties',
      label: 'Property Listings',
      icon: 'Building',
      component: PropertyListingsTab
    },
    {
      id: 'tours',
      label: 'Tour Packages',
      icon: 'MapPin',
      component: TourPackagesTab
    },
    {
      id: 'users',
      label: 'User Accounts',
      icon: 'UserCheck',
      component: UserAccountsTab
    },
    {
      id: 'orders',
      label: 'Order Intelligence',
      icon: 'ShoppingCart',
      component: OrderIntelligenceTab
    }
  ];

  const stats = {
    totalPending: 47,
    underReview: 23,
    approved: 156,
    rejected: 12
  };

  // Auto-refresh data every 10 minutes
  useEffect(() => {
    const interval = setInterval(() => {
      setLastSync(new Date());
      // Trigger data refresh for active component
    }, 600000);

    return () => clearInterval(interval);
  }, []);

  const handleManualSync = () => {
    setLastSync(new Date());
    // Trigger manual data refresh
  };

  const handleExportReport = () => {
    // Export approval reports and audit trails
    console.log('Exporting verification report...');
  };

  const ActiveComponent = tabs?.find(tab => tab?.id === activeTab)?.component;

  return (
    <Layout>
      <div className="h-full bg-background">
        {/* Header */}
        <div className="border-b border-border bg-card">
          <div className="px-6 py-4">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
              <div>
                <h1 className="text-2xl font-bold text-foreground">Verifications & Approvals</h1>
                <p className="text-sm text-muted-foreground">
                  Centralized administrative hub for reviewing and approving marketplace submissions
                </p>
              </div>

              {/* Header Actions */}
              <div className="flex items-center gap-3">
                <div className="text-xs text-muted-foreground">
                  Last sync: {lastSync?.toLocaleTimeString()}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleManualSync}
                  className="flex items-center gap-2"
                >
                  <Icon name="RefreshCw" size={14} />
                  Sync
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleExportReport}
                  className="flex items-center gap-2"
                >
                  <Icon name="Download" size={14} />
                  Export
                </Button>
              </div>
            </div>

            {/* Statistics Cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-4">
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs font-medium text-yellow-800">Pending Review</p>
                    <p className="text-lg font-bold text-yellow-900">{stats?.totalPending}</p>
                  </div>
                  <Icon name="Clock" size={20} className="text-yellow-600" />
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs font-medium text-blue-800">Under Review</p>
                    <p className="text-lg font-bold text-blue-900">{stats?.underReview}</p>
                  </div>
                  <Icon name="Eye" size={20} className="text-blue-600" />
                </div>
              </div>

              <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs font-medium text-green-800">Approved</p>
                    <p className="text-lg font-bold text-green-900">{stats?.approved}</p>
                  </div>
                  <Icon name="CheckCircle" size={20} className="text-green-600" />
                </div>
              </div>

              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs font-medium text-red-800">Rejected</p>
                    <p className="text-lg font-bold text-red-900">{stats?.rejected}</p>
                  </div>
                  <Icon name="XCircle" size={20} className="text-red-600" />
                </div>
              </div>
            </div>
          </div>

          {/* Tab Navigation */}
          <div className="px-6">
            <div className="flex space-x-1 overflow-x-auto pb-px">
              {tabs?.map((tab) => (
                <button
                  key={tab?.id}
                  onClick={() => setActiveTab(tab?.id)}
                  className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-t-lg whitespace-nowrap transition-colors ${
                    activeTab === tab?.id
                      ? 'bg-background text-foreground border-b-2 border-primary'
                      : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                  }`}
                >
                  <Icon name={tab?.icon} size={16} />
                  {tab?.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Tab Content */}
        <div className="flex-1 overflow-hidden">
          {ActiveComponent && <ActiveComponent />}
        </div>
      </div>
    </Layout>
  );
};

export default VerificationsApprovals;